/**
 * PvPTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function PvPTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [pals, setPals] = useState([]);
  const [players, setPlayers] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [viewing, setViewing] = useState(null);

  const refresh = async () => {
    try {
      const [boxRes, playersRes, inRes, outRes] = await Promise.all([
        api.get("/palgame/box"),
        api.get("/palgame/pvp/players"),
        api.get("/palgame/pvp/incoming"),
        api.get("/palgame/pvp/outgoing"),
      ]);
      setPals(boxRes.data.pals || []);
      setPlayers(playersRes.data.players || []);
      setIncoming(inRes.data.challenges || []);
      setOutgoing(outRes.data.challenges || []);
    } catch (e) {
      toast.error(apiError(e, "Failed to load PvP arena"));
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decline = async (id) => {
    try {
      await api.post(`/palgame/pvp/${id}/respond`, { action: "decline" });
      toast.success("Challenge declined");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cancel = async (id) => {
    try {
      await api.post(`/palgame/pvp/${id}/cancel`);
      toast.success("Challenge cancelled — wager refunded");
      refreshProfile();
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="pvp-tab">
      <div className="flex items-center justify-between">
        <h3 className="label">PvP Arena</h3>
        <button onClick={() => setShowCreate(true)} className="btn btn-sm" data-testid="pvp-new-btn">+ Challenge</button>
      </div>
      <p className="text-xs text-[var(--muted)] -mt-4">
        Pick a pal, set a wager, fight another player. Wagers are held in escrow — winner takes the full pot.
        Challenges expire after 10 min.
      </p>

      {incoming.length > 0 && (
        <div>
          <div className="label mb-2 text-[var(--teal)]">Incoming Challenges ({incoming.length})</div>
          <div className="space-y-2">
            {incoming.map((c) => (
              <div key={c.id} className="glass-card p-4 border-[var(--teal)]" data-testid={`pvp-incoming-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <PalPortrait name={c.challenger_pal} size="w-12 h-12" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{c.challenger_name} sent {c.challenger_pal}</div>
                      <div className="text-[10px] text-[var(--muted)] inline-flex items-center gap-1">Wager: <ChilletCoinIcon size={11}/> {c.wager.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setRespondingTo(c)} className="btn btn-sm" data-testid={`pvp-accept-${c.id}`}>Pick Pal & Fight</button>
                    <button onClick={() => decline(c.id)} className="btn btn-sm btn-secondary" data-testid={`pvp-decline-${c.id}`}>Decline</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {outgoing.length === 0 && incoming.length === 0 ? (
        <div className="glass-card p-8 text-center text-[var(--muted)] text-sm">
          No challenges yet. Click "+ Challenge" to start a fight.
        </div>
      ) : (
        <div>
          <div className="label mb-2">Recent Battles</div>
          <div className="space-y-2">
            {outgoing.map((c) => (
              <PvPHistoryRow key={c.id} c={c} onCancel={cancel} onView={() => setViewing(c.id)} />
            ))}
          </div>
        </div>
      )}

      {showCreate && (
        <NewPvPModal
          pals={pals}
          players={players}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refreshProfile(); refresh(); }}
        />
      )}
      {respondingTo && (
        <RespondPvPModal
          challenge={respondingTo}
          pals={pals}
          onClose={() => setRespondingTo(null)}
          onResolved={(result) => { setRespondingTo(null); setViewing(result?.challenge_id); refreshProfile(); refresh(); }}
        />
      )}
      {viewing && <PvPResultModal id={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function PvPHistoryRow({ c, onCancel, onView }) {
  const statusColor = {
    pending: "var(--teal)",
    complete: c.i_won ? "#66BB6A" : "#FF6B35",
    declined: "var(--muted)",
    expired: "var(--muted)",
    cancelled: "var(--muted)",
  }[c.status] || "var(--muted)";

  return (
    <div className="glass-card p-4 flex items-center gap-3" data-testid={`pvp-row-${c.id}`}>
      <div className="flex items-center gap-2">
        <PalPortrait name={c.challenger_pal} size="w-10 h-10" />
        <span className="text-xs text-[var(--muted)]">vs</span>
        {c.opponent_pal ? <PalPortrait name={c.opponent_pal} size="w-10 h-10" /> : <span className="text-xs text-[var(--muted)]">?</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{c.i_am_challenger ? "You" : c.other_name} → {c.i_am_challenger ? c.other_name : "you"}</div>
        <div className="flex items-center gap-2 text-[10px] text-[var(--muted)]">
          <span style={{ color: statusColor, border: `1px solid ${statusColor}66`, padding: "1px 6px", borderRadius: 4 }}>{c.status}</span>
          {c.wager > 0 && <span className="inline-flex items-center gap-1"><ChilletCoinIcon size={11}/> {c.wager.toLocaleString()}</span>}
          {c.i_won === true && <span className="text-[var(--teal)]">VICTORY</span>}
          {c.i_won === false && <span className="text-[var(--warning)]">defeat</span>}
        </div>
      </div>
      {c.status === "pending" && c.i_am_challenger && (
        <button onClick={() => onCancel(c.id)} className="btn btn-sm btn-secondary" data-testid={`pvp-cancel-${c.id}`}>Cancel</button>
      )}
      {c.status === "complete" && (
        <button onClick={onView} className="btn btn-sm" data-testid={`pvp-view-${c.id}`}>Replay</button>
      )}
    </div>
  );
}

function NewPvPModal({ pals, players, onClose, onCreated }) {
  const toast = useToast();
  const [opponentId, setOpponentId] = useState("");
  const [palName, setPalName] = useState("");
  const [wager, setWager] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!opponentId) return toast.error("Pick an opponent");
    if (!palName) return toast.error("Pick a pal");
    setBusy(true);
    try {
      await api.post("/palgame/pvp/challenge", {
        opponent_user_id: opponentId,
        challenger_pal: palName,
        wager: Number(wager) || 0,
      });
      toast.success("Challenge sent!");
      onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="pvp-new-modal">
      <div onClick={(e) => e.stopPropagation()} className="glass-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl glow-teal mb-4" style={{ fontFamily: "'Cinzel Decorative', serif" }}>New PvP Challenge</h3>
        <label className="label">Opponent</label>
        <select value={opponentId} onChange={(e) => setOpponentId(e.target.value)} className="input mb-3 w-full" data-testid="pvp-opponent-select">
          <option value="">— pick a player —</option>
          {players.map((p) => <option key={p.user_id} value={p.user_id}>{p.name} · {p.pal_count} pals</option>)}
        </select>
        <label className="label">Your Fighting Pal</label>
        <select value={palName} onChange={(e) => setPalName(e.target.value)} className="input mb-3 w-full" data-testid="pvp-pal-select">
          <option value="">— pick a pal —</option>
          {pals.map((p) => <option key={p.pal_name} value={p.pal_name}>{p.pal_name} (Lv {p.level})</option>)}
        </select>
        <label className="label">Wager (held in escrow, winner takes pot)</label>
        <input type="number" min="0" value={wager} onChange={(e) => setWager(e.target.value)} placeholder="0 = no wager" className="input mb-4 w-full" data-testid="pvp-wager-input" />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn btn-sm btn-secondary" data-testid="pvp-modal-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn btn-sm" data-testid="pvp-modal-submit">{busy ? "Sending…" : "Send Challenge"}</button>
        </div>
      </div>
    </div>
  );
}

function RespondPvPModal({ challenge, pals, onClose, onResolved }) {
  const toast = useToast();
  const [palName, setPalName] = useState("");
  const [busy, setBusy] = useState(false);

  const fight = async () => {
    if (!palName) return toast.error("Pick a pal");
    setBusy(true);
    try {
      const { data } = await api.post(`/palgame/pvp/${challenge.id}/respond`, {
        action: "accept",
        opponent_pal: palName,
      });
      if (data.i_won) {
        toast.success(`Victory! +${data.wager_paid_out || 0} skulls`);
        firePalgamePayout(data.wager_paid_out || 0, "PVP VICTORY");
      } else {
        toast.error("Defeat — better luck next time");
        firePalgameDefeat(challenge?.wager || 0, "PVP DEFEAT");
      }
      onResolved({ challenge_id: challenge.id });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="pvp-respond-modal">
      <div onClick={(e) => e.stopPropagation()} className="glass-card max-w-md w-full p-6">
        <h3 className="text-xl glow-teal mb-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Accept Challenge</h3>
        <div className="flex items-center gap-3 mb-4 p-3 rounded bg-black/30 border border-[var(--teal-soft)]">
          <PalPortrait name={challenge.challenger_pal} size="w-14 h-14" />
          <div>
            <div className="text-sm">{challenge.challenger_name} sent <strong>{challenge.challenger_pal}</strong></div>
            <div className="text-xs text-[var(--muted)] inline-flex items-center gap-1">Wager: <ChilletCoinIcon size={12}/> {challenge.wager.toLocaleString()}</div>
          </div>
        </div>
        <label className="label">Your Fighting Pal</label>
        <select value={palName} onChange={(e) => setPalName(e.target.value)} className="input mb-4 w-full" data-testid="pvp-respond-pal-select">
          <option value="">— pick a pal —</option>
          {pals.map((p) => <option key={p.pal_name} value={p.pal_name}>{p.pal_name} (Lv {p.level})</option>)}
        </select>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn btn-sm btn-secondary">Back</button>
          <button onClick={fight} disabled={busy} className="btn btn-sm" data-testid="pvp-respond-fight">{busy ? "Fighting…" : "Fight!"}</button>
        </div>
      </div>
    </div>
  );
}

function PvPResultModal({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/palgame/pvp/${id}`).then(({ data }) => setData(data));
  }, [id]);

  if (!data) return null;
  const log = data.battle_log || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="pvp-result-modal">
      <div onClick={(e) => e.stopPropagation()} className="glass-card max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className={`text-2xl mb-4 ${data.i_won ? "glow-teal" : "text-[var(--warning)]"}`}>
          {data.i_won ? "Victory!" : data.i_won === false ? "Defeat" : data.status}
        </h3>
        <div className="grid grid-cols-3 items-center gap-3 mb-5">
          <div className="text-center">
            <PalPortrait name={data.challenger_pal} size="w-20 h-20 mx-auto" />
            <div className="text-xs mt-1 truncate">{data.challenger_pal}</div>
            <div className="text-[10px] text-[var(--muted)]">{data.challenger_name}</div>
          </div>
          <div className="text-center text-[var(--muted)] text-xs">VS</div>
          <div className="text-center">
            {data.opponent_pal && <PalPortrait name={data.opponent_pal} size="w-20 h-20 mx-auto" />}
            <div className="text-xs mt-1 truncate">{data.opponent_pal || "?"}</div>
            <div className="text-[10px] text-[var(--muted)]">{data.opponent_name}</div>
          </div>
        </div>
        {data.wager > 0 && (
          <div className="text-center text-sm mb-4">
            Wager: <span className="glow-gold inline-flex items-center gap-1"><ChilletCoinIcon size={12}/> {data.wager.toLocaleString()}</span> · Pot: <span className="inline-flex items-center gap-1"><ChilletCoinIcon size={12}/> {(data.wager * 2).toLocaleString()}</span>
          </div>
        )}
        {log.length > 0 && (
          <div className="rounded bg-black/40 border border-white/5 p-3 max-h-72 overflow-y-auto">
            <div className="label mb-2">Battle Log</div>
            <div className="space-y-1 text-xs">
              {log.map((entry, i) => {
                const eff = entry.effectiveness === "super_effective" ? "🔥" : entry.effectiveness === "not_effective" ? "🛡️" : "";
                return (
                  <div key={i} className="flex justify-between">
                    <span>
                      <span className="text-[var(--muted)]">T{entry.turn}</span>{" "}
                      <span className={entry.actor === "challenger" ? "text-[var(--teal)]" : "text-[var(--warning)]"}>{entry.actor}</span>{" "}
                      used <strong>{entry.skill}</strong>
                    </span>
                    <span>−{entry.damage} {eff}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <button onClick={onClose} className="btn btn-sm w-full mt-4">Close</button>
      </div>
    </div>
  );
}

// ── How It Works (Help) tab ──────────────────────────────────────────
// Single-source explanation of every Pal Game surface, in tab order.
